/**
 * Chat Socket — Socket.IO client for the `/chat` namespace.
 *
 * Separate from the default-namespace websocket.ts used for progress/notifications.
 * Connects through the same origin with cookie auth (fairgen-jwt).
 */

import { io, Socket } from 'socket.io-client'
import type {
  ChatRoomParticipantReadyEvent,
  ChatRoomParticipantImageEvent,
  ChatRoomReadyEvent,
  ChatRoomMessageEvent,
  ChatRoomParticipantStateEvent,
  ChatRoomTurnCompleteEvent,
  ChatRoomTitleUpdatedEvent,
  ChatRoomClosedEvent,
  ChatRoomReactionsEvent,
  ChatRoomOpeningQuestionEvent,
} from '@digital-twins/shared-types'

const log = (...args: Array<unknown>) => { if (import.meta.env.DEV) console.log(...args) }
const warn = (...args: Array<unknown>) => { if (import.meta.env.DEV) console.warn(...args) }
const logError = (...args: Array<unknown>) => { if (import.meta.env.DEV) console.error(...args) }

let chatSocket: Socket | null = null
const activeRooms = new Set<string>()
const roomJoiningListeners = new Set<(roomId: string) => void>()

export interface RoomJoinedEvent {
  roomId: string
  thinkingParticipantIds?: string[]
  turnId?: string
}

function emitJoin(ws: Socket, roomId: string) {
  for (const listener of roomJoiningListeners) listener(roomId)
  ws.emit('join-room', { roomId })
}

export function onRoomJoining(callback: (roomId: string) => void) {
  roomJoiningListeners.add(callback)
  return () => { roomJoiningListeners.delete(callback) }
}

export function onRoomJoined(callback: (event: RoomJoinedEvent) => void) {
  const ws = connectChatSocket()
  ws.on('room-joined', callback)
  return () => { ws.off('room-joined', callback) }
}
/** Guest session JWT for shared-link viewers, who have no fairgen-jwt cookie. */
let authToken: string | null = null

/**
 * Set (or clear) the explicit handshake token. Platform users authenticate via
 * the cookie and never call this; guests on /share/:token must, because the SPA
 * auth state — and the cookie — are deliberately cleared on share routes.
 * Changing the token tears down the singleton so the next connect re-handshakes
 * with the new identity.
 */
export function setChatSocketAuthToken(token: string | null) {
  if (authToken === token) return
  authToken = token
  if (chatSocket) {
    chatSocket.disconnect()
    chatSocket = null
  }
}

export function connectChatSocket(): Socket {
  if (!chatSocket) {
    const baseUrl =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : ''

    chatSocket = io(`${baseUrl}/chat`, {
      transports: ['websocket', 'polling'],
      path: '/api/socket.io/',
      autoConnect: true,
      withCredentials: true,
      ...(authToken ? { auth: { token: authToken } } : {}),
    })

    chatSocket.on('connect', () => {
      log('[ChatSocket] connected, id:', chatSocket?.id)
      for (const roomId of activeRooms) {
        log('[ChatSocket] re-joining room on reconnect:', roomId)
        if (chatSocket) emitJoin(chatSocket, roomId)
      }
    })

    chatSocket.on('disconnect', (reason) => {
      warn('[ChatSocket] disconnected:', reason)
    })

    chatSocket.on('connect_error', (error) => {
      logError('[ChatSocket] connect_error:', error.message)
    })

    chatSocket.on('error', (data: unknown) => {
      logError('[ChatSocket] server error:', data)
    })
  } else if (!chatSocket.connected) {
    chatSocket.connect()
  }

  return chatSocket
}

export function disconnectChatSocket() {
  if (chatSocket) {
    chatSocket.disconnect()
    chatSocket = null
  }
  activeRooms.clear()
}

export function joinRoom(roomId: string) {
  activeRooms.add(roomId)
  const ws = connectChatSocket()
  log('[ChatSocket] joinRoom:', roomId, 'connected:', ws.connected)
  // A disconnected socket joins from its connect handler. Buffering this emit
  // as well would produce duplicate join snapshots on the first connection.
  if (ws.connected) emitJoin(ws, roomId)
}

export function leaveRoom(roomId: string) {
  activeRooms.delete(roomId)
  const ws = connectChatSocket()
  ws.emit('leave-room', { roomId })
}

export function onParticipantReady(
  callback: (event: ChatRoomParticipantReadyEvent) => void,
) {
  const ws = connectChatSocket()
  ws.on('participant-ready', callback)
  return () => { ws.off('participant-ready', callback) }
}

export function onParticipantImage(
  callback: (event: ChatRoomParticipantImageEvent) => void,
) {
  const ws = connectChatSocket()
  ws.on('participant-image', callback)
  return () => { ws.off('participant-image', callback) }
}

/** `opening-question`: the room's suggested opening question, once generated. */
export function onOpeningQuestion(
  callback: (event: ChatRoomOpeningQuestionEvent) => void,
) {
  const ws = connectChatSocket()
  ws.on('opening-question', callback)
  return () => { ws.off('opening-question', callback) }
}

export function onRoomReady(
  callback: (event: ChatRoomReadyEvent) => void,
) {
  const ws = connectChatSocket()
  ws.on('room-ready', callback)
  return () => { ws.off('room-ready', callback) }
}

export function onMessage(
  callback: (event: ChatRoomMessageEvent) => void,
) {
  const ws = connectChatSocket()
  const wrapped = (event: ChatRoomMessageEvent) => {
    log('[ChatSocket] message received:', event.roomId, event.message?.senderType, event.message?.id)
    callback(event)
  }
  ws.on('message', wrapped)
  return () => { ws.off('message', wrapped) }
}

export function onParticipantState(
  callback: (event: ChatRoomParticipantStateEvent) => void,
) {
  const ws = connectChatSocket()
  ws.on('participant-state', callback)
  return () => { ws.off('participant-state', callback) }
}

export function onTurnComplete(
  callback: (event: ChatRoomTurnCompleteEvent) => void,
) {
  const ws = connectChatSocket()
  ws.on('turn-complete', callback)
  return () => { ws.off('turn-complete', callback) }
}

/**
 * C13: `message-reactions` carries the room's full resolved reactions snapshot.
 * Snapshots may arrive out of order; callers apply one only when its
 * `revision` is greater than the last applied.
 */
export function onMessageReactions(
  callback: (event: ChatRoomReactionsEvent) => void,
) {
  const ws = connectChatSocket()
  ws.on('message-reactions', callback)
  return () => { ws.off('message-reactions', callback) }
}

export function onRoomClosed(
  callback: (event: ChatRoomClosedEvent) => void,
) {
  const ws = connectChatSocket()
  ws.on('closed', callback)
  return () => { ws.off('closed', callback) }
}

export function onTitleUpdated(
  callback: (event: ChatRoomTitleUpdatedEvent) => void,
) {
  const ws = connectChatSocket()
  ws.on('title-updated', callback)
  return () => { ws.off('title-updated', callback) }
}

export function getChatSocket(): Socket | null {
  return chatSocket
}
